import {
    ActiveElement,
    Chart,
    ChartEvent,
    ScaleOptions,
    Plugin,
    TooltipOptions,
    ChartOptions,
} from "chart.js";
import { ZoomPluginOptions } from "chartjs-plugin-zoom/types/options";
import {
    calculateCrosshairLength,
} from "helpers/tempProfileChartHelpers";
import { theme } from "styling/theme";
import { TempProfileChartProps } from "./TempProfileChartProps";

declare module "chart.js" {
    interface Chart {
        customState: {
            prevStatus: "out-of-range" | "in-range";
            currentStatus: "out-of-range" | "in-range";
            hideCrosshair: boolean;
            x: number;
        };
    }
}

const zoomOptions: ZoomPluginOptions = {
    zoom: {
        wheel: {
            enabled: true,
        },

        pinch: {
            enabled: true,
        },

        drag: {
            enabled: true,
        },

        mode: "xy",

        onZoomStart({ chart }) {
            chart.customState.hideCrosshair = true;
        },
    },

    pan: {
        enabled: true,
        mode: "xy",
        modifierKey: "ctrl",

        onPanStart({ chart }) {
            chart.customState.hideCrosshair = true;
            return true;
        },
    },

    // limits: {
    //     x: {
    //         minRange: 60 * 60 * 1000 * 12,
    //     },
    //     y: {
    //         minRange: 20,
    //     },
    // },
};

const xAxisOptions: ScaleOptions<"time"> = {
    grid: {
        color: theme.colors.neutrals.neutral200,
        tickColor: "transparent",
    },

    type: "time",

    ticks: {
        autoSkip: true,
        autoSkipPadding: 20,
        maxRotation: 0,
        color: theme.colors.neutrals.neutral300,
    },

    time: {
        minUnit: "hour",
    },
};

const yAxisOptions: ScaleOptions<"linear"> = {
    grid: {
        tickColor: "transparent",
        color: theme.colors.neutrals.neutral200,
    },

    ticks: {
        color: theme.colors.neutrals.neutral300,
    },
};

const tooltipOptions: Partial<TooltipOptions<"line">> = {
    enabled: false,
    mode: "index",
    intersect: false,

    external({ chart, tooltip }) {
        let tooltipEl = document.getElementById("chartjs-tooltip");

        if (!tooltipEl) {
            tooltipEl = document.createElement("div");
            tooltipEl.id = "chartjs-tooltip";
            document.body.appendChild(tooltipEl);
        }

        if (tooltip.opacity === 0 || chart.customState.x === null) {
            tooltipEl.style.opacity = "0";
            return;
        }

        const canvasPosition = chart.canvas.getBoundingClientRect();

        const left =
            canvasPosition.left + chart.customState.x + window.pageXOffset;

        const top = canvasPosition.top + window.pageYOffset - 20;

        tooltipEl.innerHTML = `
        <div class="temp-profile-chart-tooltip-content">
        <div class="tpc-max-temp-legend-circle"></div>
        <div class="tpc-temp-legend-caption tpc-temp-legend-first">
        <span class="temp-value">${tooltip.dataPoints[0].formattedValue}°C</span>
        <span class="datetime-value">${tooltip.dataPoints[0].label}</span>
        </div>
        <div class="tpc-min-temp-legend-circle"></div>
        <div class="tpc-temp-legend-caption">
        <span class="temp-value">${tooltip.dataPoints[2].formattedValue}°C</span>
        <span class="datetime-value">${tooltip.dataPoints[2].label}</span>
        </div>
        </div>
        `;

        tooltipEl.style.opacity = "1";
        tooltipEl.style.left = `${left}px`;
        tooltipEl.style.top = `${top}px`;
    },
};

export const plugins: Plugin[] = [
    {
        id: "crosshair",

        afterInit(chart: Chart) {
            chart.customState = {
                x: 0,
                hideCrosshair: false,
                currentStatus: "in-range",
                prevStatus: "in-range",
            };
        },

        afterDraw(chart: Chart) {
            if (chart.customState.hideCrosshair) {
                return;
            }

            chart.ctx.beginPath();

            chart.ctx.moveTo(
                chart.customState.x,
                chart.scales.y.getPixelForValue(chart.scales.y.max)
            );

            chart.ctx.lineWidth = calculateCrosshairLength(chart);

            chart.ctx.strokeStyle = "rgba(39, 10, 220, 0.3)";

            chart.ctx.lineTo(
                chart.customState.x,
                chart.scales.y.getPixelForValue(chart.scales.y.min)
            );

            chart.ctx.stroke();
        },
    },
];

export const constructOptions = ({
    tempItems,
    minTimestamp,
    maxTimestamp,
}: TempProfileChartProps) => {
    const onHover = (
        e: ChartEvent,
        elements: ActiveElement[],
        chart: Chart
    ) => {
        if (e.x === null) {
            return;
        }

        const valueForPixel = chart.scales.x.getValueForPixel(e.x);

        if (valueForPixel === undefined) {
            return;
        }

        const updateStatus = (status: "in-range" | "out-of-range") => {
            chart.customState.currentStatus = status;
            if (
                chart.customState.currentStatus !== chart.customState.prevStatus
            ) {
                chart.update();
                chart.customState.prevStatus = chart.customState.currentStatus;
            }
        };

        const tooltipEl = document.getElementById("chartjs-tooltip");

        if (valueForPixel > minTimestamp && valueForPixel < maxTimestamp) {
            if (elements[0] === undefined) {
                chart.customState.hideCrosshair = true;
            } else {
                chart.customState.x = elements[0].element.x;
                chart.customState.hideCrosshair = false;

                updateStatus("in-range");

                if (tooltipEl) {
                    tooltipEl.style.opacity = "1";
                }
            }
        } else {
            updateStatus("out-of-range");

            if (tooltipEl) {
                tooltipEl.style.opacity = "0";
            }

            chart.customState.hideCrosshair = true;
        }
    };

    const options: ChartOptions<"line"> = {
        animation: false,
        parsing: false,

        elements: {
            point: {
                radius: 0,
            },
        },

        plugins: {
            zoom: zoomOptions,

            legend: {
                display: false,
            },

            tooltip: tooltipOptions,
        },

        interaction: {
            mode: "index",
            intersect: false,
        },

        onHover,

        scales: {
            x: xAxisOptions,
            y: yAxisOptions,
        },
    };

    return options;
};
